import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";

const DISPOSABLE_DOMAINS = [
    "mailinator.com",
    "yopmail.com",
    "tempmail.com",
    "trashmail.com",
    "dispostable.com",
    "guerrillamail.com",
    "sharklasers.com",
    "getairmail.com",
    "maildrop.cc",
    "mintemail.com",
    "jetable.org",
    "temp-mail.org",
    "disposable.com",
    "fakeinbox.com",
    "tempmailaddress.com",
    "throwawaymail.com",
    "10minutemail.com"
];

const QuestionnaireModal = ({ isOpen, onClose, selectedPlan }) => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        plan: selectedPlan || "Explorer",
        budget: "$5,000 - $25,000",
        message: "",
        needs: []
    });

    const [status, setStatus] = useState("idle"); // 'idle' | 'sending' | 'success' | 'error'
    const [emailError, setEmailError] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (selectedPlan) {
            setFormData(prev => ({ ...prev, plan: selectedPlan }));
        }
    }, [selectedPlan]);

    // Reset status and error when modal is opened
    useEffect(() => {
        if (isOpen) {
            setStatus("idle");
            setEmailError("");
            setCopied(false);
        }
    }, [isOpen]);

    // Focus trap & Escape key listener for Accessibility
    useEffect(() => {
        if (!isOpen) return;

        const modalElement = document.getElementById("questionnaire-modal-box");
        if (modalElement) {
            const firstInput = modalElement.querySelector("input");
            if (firstInput) {
                // Short timeout to let the anim scale play out before focusing
                const timer = setTimeout(() => firstInput.focus(), 100);
                return () => clearTimeout(timer);
            }
        }

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
                return;
            }

            if (e.key === "Tab" && modalElement) {
                const focusableElements = modalElement.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (focusableElements.length === 0) return;
                
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) { // Shift + Tab
                    if (document.activeElement === firstElement) {
                        lastElement.focus();
                        e.preventDefault();
                    }
                } else { // Tab
                    if (document.activeElement === lastElement) {
                        firstElement.focus();
                        e.preventDefault();
                    }
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    const budgetOptions = [
        "Under $5,000",
        "$5,000 - $25,000",
        "$25,000 - $100,000",
        "$100,000+"
    ];

    const needOptions = [
        { id: "strategy", label: "Enterprise AI & Strategy (Tier 1)" },
        { id: "mobile", label: "On-Device AI & Native Mobile (Tier 2)" },
        { id: "smb", label: "SMB Operational Automation (Tier 3)" },
        { id: "llm", label: "Custom LLM Fine-Tuning & RAG" },
        { id: "engineering", label: "Full-Stack Software Engineering" }
    ];

    const handleCheckboxChange = (label) => {
        setFormData(prev => {
            const needs = prev.needs.includes(label)
                ? prev.needs.filter(n => n !== label)
                : [...prev.needs, label];
            return { ...prev, needs };
        });
    };

    const validateEmail = (email) => {
        if (!email) {
            return "Email is required.";
        }
        
        // Regex format validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email)) {
            return "Please enter a valid email address.";
        }
        
        // Disposable domain blocklist validation
        const domain = email.split("@")[1]?.toLowerCase();
        if (DISPOSABLE_DOMAINS.includes(domain)) {
            return "Please provide a valid work email. Disposable emails are not allowed.";
        }
        
        return "";
    };

    const getEmailContent = () => {
        const subject = `Jibu Labs Inquiry: ${formData.plan} Plan - ${formData.company || formData.name}`;
        const body = `Jibu Labs Consultation Request\n` +
                     `=================================\n\n` +
                     `Name: ${formData.name}\n` +
                     `Work Email: ${formData.email}\n` +
                     `Company: ${formData.company || 'Not Specified'}\n` +
                     `Plan Selected: ${formData.plan}\n` +
                     `Budget Range: ${formData.budget}\n\n` +
                     `AI & Engineering Needs:\n` +
                     `${formData.needs.length > 0 ? formData.needs.map(n => `- ${n}`).join('\n') : '- None selected'}\n\n` +
                     `Message / Project Details:\n` +
                     `${formData.message || 'No additional details provided.'}\n`;
        return { subject, body };
    };

    const copyTextFallback = (text) => {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.position = "fixed";
            textArea.style.top = "0";
            textArea.style.left = "0";
            textArea.style.opacity = "0";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            return successful;
        } catch (err) {
            console.error("Fallback copy failed:", err);
            return false;
        }
    };

    const handleCopyToClipboard = () => {
        const { subject, body } = getEmailContent();
        const fullText = `Subject: ${subject}\n\n${body}`;
        
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(fullText).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 3000);
            }).catch(err => {
                console.error("Failed to copy:", err);
                const ok = copyTextFallback(fullText);
                if (ok) {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 3000);
                }
            });
        } else {
            const ok = copyTextFallback(fullText);
            if (ok) {
                setCopied(true);
                setTimeout(() => setCopied(false), 3000);
            }
        }
    };

    const handleMailtoFallbackClick = () => {
        // Automatically copy to clipboard as a helpful backup
        const { subject, body } = getEmailContent();
        const fullText = `Subject: ${subject}\n\n${body}`;
        
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(fullText).then(() => {
                setCopied(true);
            }).catch(err => {
                console.error("Failed to auto-copy to clipboard:", err);
                copyTextFallback(fullText);
                setCopied(true);
            });
        } else {
            copyTextFallback(fullText);
            setCopied(true);
        }

        // Close after a delay so they see the status feedback
        setTimeout(() => {
            setFormData({
                name: "",
                email: "",
                company: "",
                plan: selectedPlan || "Explorer",
                budget: "$5,000 - $25,000",
                message: "",
                needs: []
            });
            setCopied(false);
            setStatus("idle");
            onClose();
        }, 2500);
    };

    const handleMailtoFallback = () => {
        const { subject, body } = getEmailContent();
        const mailtoUrl = `mailto:info@jibulabs.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        
        // Copy to clipboard as helper
        const fullText = `Subject: ${subject}\n\n${body}`;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(fullText).catch(err => {
                console.error(err);
                copyTextFallback(fullText);
            });
        } else {
            copyTextFallback(fullText);
        }

        window.location.href = mailtoUrl;

        // Reset/clear the form data
        setFormData({
            name: "",
            email: "",
            company: "",
            plan: selectedPlan || "Explorer",
            budget: "$5,000 - $25,000",
            message: "",
            needs: []
        });

        // Close after a tiny timeout to ensure the browser processes the mailto redirection before unmounting
        setTimeout(() => {
            onClose();
        }, 500);
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        
        const errorMsg = validateEmail(formData.email);
        if (errorMsg) {
            setEmailError(errorMsg);
            return;
        }

        // Show loading spinner animation
        setStatus("sending");

        const encode = (data) => {
            return Object.keys(data)
                .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
                .join("&");
        };

        const payload = {
            "form-name": "consultation",
            name: formData.name,
            email: formData.email,
            company: formData.company || "Not Specified",
            plan: formData.plan,
            budget: formData.budget,
            needs: formData.needs.length > 0 ? formData.needs.join(", ") : "None selected",
            message: formData.message || "No additional details provided."
        };

        try {
            const response = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode(payload)
            });

            if (response.ok) {
                setStatus("success");
                
                // Clear the form data
                setFormData({
                    name: "",
                    email: "",
                    company: "",
                    plan: selectedPlan || "Explorer",
                    budget: "$5,000 - $25,000",
                    message: "",
                    needs: []
                });

                // Auto-close modal after 3 seconds success display
                setTimeout(() => {
                    setStatus("idle");
                    onClose();
                }, 3000);
            } else {
                throw new Error("Netlify form submission rejected.");
            }
        } catch (error) {
            console.error("Netlify Form submission failed:", error);
            setStatus("error");
        }
    };

    if (!isOpen) return null;

    const { subject, body } = getEmailContent();
    const mailtoUrl = `mailto:info@jibulabs.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                {/* Backdrop overlay */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                />

                {/* Modal box */}
                <motion.div 
                    id="questionnaire-modal-box"
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    transition={{ type: "spring", damping: 25, stiffness: 350 }}
                    className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-[#202222] rounded-3xl border border-slate-200 dark:border-slate-800 p-6 lg:p-8 shadow-2xl transition-colors duration-300 z-10"
                >
                    {/* Close button - hide during sending/success */}
                    {status !== "sending" && status !== "success" && (
                        <button 
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                            aria-label="Close modal"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}

                    <div className="space-y-6">
                        {status === "idle" && (
                            <>
                                {/* Title */}
                                <div>
                                    <span className="text-xs font-bold text-forest uppercase tracking-widest block mb-1">Get Started</span>
                                    <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white leading-tight">
                                        Tell us about your needs
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-light mt-1">
                                        We will customize your consulting session. Hitting send routes your inquiry directly to our team in the background.
                                    </p>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit} className="space-y-4 text-sm text-left">
                                    {/* Row 1: Name and Email */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label htmlFor="modal-name" className="block text-xs font-semibold text-slate-600 dark:text-slate-350">Name</label>
                                            <input 
                                                id="modal-name"
                                                type="text" 
                                                required
                                                value={formData.name}
                                                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#191a1a] text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-forest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                                placeholder="Jane Doe"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label htmlFor="modal-email" className="block text-xs font-semibold text-slate-600 dark:text-slate-355">Work Email</label>
                                            <input 
                                                id="modal-email"
                                                type="email" 
                                                required
                                                value={formData.email}
                                                onChange={e => {
                                                    setFormData(prev => ({ ...prev, email: e.target.value }));
                                                    setEmailError("");
                                                }}
                                                className={`w-full px-4 py-2.5 rounded-xl border bg-slate-50 dark:bg-[#191a1a] text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-forest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                                                    emailError ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-slate-200 dark:border-slate-800"
                                                }`}
                                                placeholder="jane@company.com"
                                            />
                                            {emailError && (
                                                <span className="text-red-500 text-xs font-semibold block mt-1">
                                                    {emailError}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: Company and Budget */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label htmlFor="modal-company" className="block text-xs font-semibold text-slate-600 dark:text-slate-350">Company Name</label>
                                            <input 
                                                id="modal-company"
                                                type="text" 
                                                value={formData.company}
                                                onChange={e => setFormData(prev => ({ ...prev, company: e.target.value }))}
                                                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#191a1a] text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-forest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                                placeholder="Acme Corp"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label htmlFor="modal-budget" className="block text-xs font-semibold text-slate-600 dark:text-slate-350">Budget Range</label>
                                            <select 
                                                id="modal-budget"
                                                value={formData.budget}
                                                onChange={e => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                                                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#191a1a] text-slate-850 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-forest transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                            >
                                                {budgetOptions.map(opt => (
                                                    <option key={opt} value={opt} className="bg-white dark:bg-[#202222]">{opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Fields: Select Plan */}
                                    <div className="space-y-1">
                                        <label htmlFor="modal-plan" className="block text-xs font-semibold text-slate-600 dark:text-slate-350">Interested Plan / Topic</label>
                                        <input 
                                            id="modal-plan"
                                            type="text" 
                                            value={formData.plan}
                                            onChange={e => setFormData(prev => ({ ...prev, plan: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#191a1a] text-slate-850 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-forest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                            placeholder="Explorer"
                                        />
                                    </div>

                                    {/* Checkboxes: Needs */}
                                    <div className="space-y-2">
                                        <span className="block text-xs font-semibold text-slate-600 dark:text-slate-350">What do you need help with?</span>
                                        <div className="space-y-2 max-h-[160px] overflow-y-auto p-2 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-[#191a1a]/50">
                                            {needOptions.map(opt => {
                                                const isChecked = formData.needs.includes(opt.label);
                                                return (
                                                    <label key={opt.id} className="flex items-center space-x-3 cursor-pointer py-1 px-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors">
                                                        <input 
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => handleCheckboxChange(opt.label)}
                                                            className="w-4 h-4 rounded text-forest focus:ring-forest border-slate-300 dark:border-slate-700 bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                                        />
                                                        <span className="text-xs text-slate-600 dark:text-slate-300 font-light">{opt.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Textarea: Details */}
                                    <div className="space-y-1">
                                        <label htmlFor="modal-message" className="block text-xs font-semibold text-slate-600 dark:text-slate-350">Tell us more about your project</label>
                                        <textarea 
                                            id="modal-message"
                                            rows={3}
                                            value={formData.message}
                                            onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#191a1a] text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-forest transition-all resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                            placeholder="Briefly describe your objectives, timeframe, or compliance needs..."
                                        />
                                    </div>

                                    {/* Submit and Cancel Buttons */}
                                    <div className="flex space-x-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 py-3 rounded-xl bg-forest hover:bg-forest-hover text-white font-semibold transition-all shadow-[0_4px_12px_rgba(5,150,105,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-forest"
                                        >
                                            Send Inquiry
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}

                        {status === "sending" && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex flex-col items-center justify-center py-12 space-y-6 text-center"
                            >
                                <div className="relative flex items-center justify-center">
                                    <div className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-lg font-bold text-slate-900 dark:text-white">Sending Inquiry</h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-light max-w-xs">
                                        Please wait while we route your details directly to our consulting team...
                                    </p>
                                </div>
                            </motion.div>
                        )}

                        {status === "success" && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex flex-col items-center justify-center py-12 space-y-6 text-center"
                            >
                                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/50 rounded-full flex items-center justify-center text-emerald-500">
                                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-xl font-bold text-slate-900 dark:text-white">Inquiry Received!</h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-light max-w-xs leading-relaxed">
                                        Thank you for reaching out. We have received your inquiry and our team will get in touch with you shortly.
                                    </p>
                                </div>
                            </motion.div>
                        )}

                        {status === "error" && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex flex-col items-center justify-center py-8 space-y-6 text-center"
                            >
                                <div className="w-16 h-16 bg-red-100 dark:bg-red-950/50 border border-red-200 dark:border-red-800/50 rounded-full flex items-center justify-center text-red-500">
                                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-lg font-bold text-slate-900 dark:text-white">Submission Failed</h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-light max-w-xs leading-relaxed">
                                        We encountered an error routing your details in the background. Don't worry, your information is safe!
                                    </p>
                                </div>
                                <div className="flex flex-col w-full space-y-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="w-full py-3 rounded-xl bg-forest hover:bg-forest-hover text-white font-semibold transition-all shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-forest"
                                    >
                                        Try Again
                                    </button>
                                    <a
                                        href={mailtoUrl}
                                        onClick={handleMailtoFallbackClick}
                                        className="w-full py-3 text-center block rounded-xl border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                    >
                                        {copied ? "✓ Copied & Opening Mail..." : "Open Default Email Composer"}
                                    </a>
                                    <button
                                        type="button"
                                        onClick={handleCopyToClipboard}
                                        className="w-full py-3 rounded-xl border border-dashed border-emerald-500/30 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/5 font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                    >
                                        {copied ? "✓ Details Copied!" : "Copy Details to Clipboard"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setStatus("idle")}
                                        className="w-full py-2 text-xs text-slate-450 hover:text-slate-650 dark:text-slate-400 dark:hover:text-slate-205 transition-all underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                                    >
                                        Edit Form Information
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default QuestionnaireModal;
